import { Box, Fade } from '@mui/material';
import { useInView } from 'react-intersection-observer';

import { CAROUSEL_EPISODES_LIMIT } from '@/constants';
import { LearningPathWithDuration, Referrer } from '@/types';

import { Carousel, CarouselProps, useCarouselScroll } from '../Carousel';
import {
  LearningPathCardDimensions,
  LearningPathCardSkeleton,
  LearningPathCardVariants,
  LearningPathVerticalCard,
} from '../LearningPathCard';

const itemsPerScroll = 2;
const gutterSpacing = 2;

interface LearningPathCardCarouselProps {
  title?: string;
  uKey: string;
  learningPaths: LearningPathWithDuration[];
  isLoading?: boolean;
  ViewAllLink?: CarouselProps['ViewAllLink'];
  referrer: Referrer;
  LpCardComponents?: {
    SuperTitle?: React.FC<{
      learningPath: LearningPathWithDuration;
      referrer?: Referrer;
    }>;
    Footer?: React.FC<{ learningPath: LearningPathWithDuration }>;
  };
}

export const LearningPathCardCarousel = ({
  title,
  uKey,
  learningPaths,
  isLoading,
  ViewAllLink,
  referrer,
  LpCardComponents,
}: LearningPathCardCarouselProps) => {
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const learningPathsList = !inView
    ? learningPaths.slice(0, 1)
    : isLoading && learningPaths.length === 0
      ? (Array.from({ length: CAROUSEL_EPISODES_LIMIT }) as [])
      : learningPaths;

  const itemWidth =
    LearningPathCardDimensions[LearningPathCardVariants.VERTICAL].width +
    gutterSpacing * 8;

  const { containerId, scroll, isNearBeginning, isNearEnd } = useCarouselScroll(
    {
      itemWidth: itemWidth * itemsPerScroll,
      uKey,
      referrer,
    },
  );

  const carouselReferrer = {
    carousel: title,
    ...referrer,
  };

  return (
    <div ref={ref}>
      <Carousel
        title={title}
        ViewAllLink={ViewAllLink}
        containerId={containerId}
        scroll={scroll}
        leftScrollIcon={!isNearBeginning}
        rightScrollIcon={!isNearEnd}>
        {learningPathsList.map((lp, index) => (
          <LearningPathCardWrapper
            key={lp?.groupId || index}
            lp={lp}
            index={index}
            referrer={carouselReferrer}
            Components={LpCardComponents}
          />
        ))}
      </Carousel>
    </div>
  );
};

const LearningPathCardWrapper = ({
  lp,
  index,
  referrer,
  Components,
}: {
  lp?: LearningPathCardCarouselProps['learningPaths'][number];
  index: number;
  referrer: LearningPathCardCarouselProps['referrer'];
  Components: LearningPathCardCarouselProps['LpCardComponents'];
}) => {
  const { Footer, SuperTitle } = Components || {};

  return !lp ? (
    <LearningPathCardSkeleton variant={LearningPathCardVariants.VERTICAL} />
  ) : (
    <Fade in timeout={Math.log10(index + 2) * 2000}>
      <Box sx={{ mr: 2 }}>
        <LearningPathVerticalCard
          learningPath={lp}
          referrer={{ ...referrer, position: index + 1 }}
          Footer={Footer}
          PinButton={SuperTitle}
        />
      </Box>
    </Fade>
  );
};
